import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import { GoogleBusinessLogo } from "../platformCatalog";
import { loadGoogleLocations } from "../platformConnectionsStorage";
import { markOnboardingComplete } from "../onboardingStorage";
import { useTranslation } from "../../../i18n";

const ClientOnboardingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locations = useMemo(() => loadGoogleLocations(), []);
  const [stepIndex, setStepIndex] = useState(0);
  const hasLocations = locations.length > 0;

  const steps = useMemo(
    () => [
      {
        id: "welcome",
        title: t("onboarding.welcomeTitle"),
        description: t("onboarding.welcomeDesc"),
        icon: "waving_hand",
      },
      {
        id: "connect",
        title: t("onboarding.connectTitle"),
        description: t("onboarding.connectDesc"),
        icon: "link",
      },
      {
        id: "sync",
        title: t("onboarding.syncTitle"),
        description: t("onboarding.syncDesc"),
        icon: "sync",
      },
      {
        id: "explore",
        title: t("onboarding.exploreTitle"),
        description: t("onboarding.exploreDesc"),
        icon: "rocket_launch",
      },
    ],
    [t]
  );

  const step = steps[stepIndex];

  const finish = () => {
    markOnboardingComplete();
    navigate("/dashboard/client", { replace: true });
  };

  const next = () => {
    if (stepIndex >= steps.length - 1) {
      finish();
      return;
    }
    setStepIndex((current) => current + 1);
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-background px-margin-mobile py-stack-lg md:px-margin-desktop">
      <div className="w-full max-w-2xl">
        <div className="mb-8 flex items-center justify-center gap-2">
          {steps.map((item, index) => (
            <div
              key={item.id}
              className={`h-2 flex-1 rounded-full transition-colors ${
                index <= stepIndex ? "bg-secondary" : "bg-surface-container-high"
              }`}
            />
          ))}
        </div>

        <div className="tonal-card rounded-2xl border border-outline-variant p-8 md:p-10">
          <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary-container">
            <MaterialIcon name={step.icon} className="text-3xl text-secondary" filled />
          </div>

          <p className="font-label-caps text-on-surface-variant">
            {t("onboarding.stepOf", { current: stepIndex + 1, total: steps.length })}
          </p>
          <h1 className="mt-2 font-headline-lg text-headline-lg text-primary">{step.title}</h1>
          <p className="mt-3 text-body-md leading-relaxed text-on-surface-variant">
            {step.description}
          </p>

          {step.id === "connect" ? (
            <div className="mt-6 rounded-xl border border-outline-variant bg-surface-container-low p-5">
              <div className="flex items-center gap-4">
                <GoogleBusinessLogo className="h-12 w-12" />
                <div>
                  <p className="font-headline-md text-primary">Google Business</p>
                  <p className="text-[12px] text-on-surface-variant">
                    {hasLocations
                      ? t("onboarding.locationsConnected", { count: locations.length })
                      : t("onboarding.notConnectedYet")}
                  </p>
                </div>
              </div>
              {!hasLocations ? (
                <Link
                  to="/dashboard/client/platforms"
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-label-sm font-medium text-on-primary"
                >
                  {t("onboarding.goToPlatforms")}
                  <MaterialIcon name="arrow_forward" className="text-[16px]" />
                </Link>
              ) : null}
            </div>
          ) : null}

          {step.id === "sync" ? (
            <div className="mt-6 rounded-xl border border-secondary/30 bg-secondary-container/20 p-4 text-body-md text-on-surface-variant">
              {hasLocations ? t("onboarding.syncConnected") : t("onboarding.syncNotConnected")}
            </div>
          ) : null}

          <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={finish}
              className="text-label-sm font-medium text-on-surface-variant hover:text-primary"
            >
              {t("onboarding.skipForNow")}
            </button>
            <div className="flex gap-3">
              {stepIndex > 0 ? (
                <button
                  type="button"
                  onClick={() => setStepIndex((current) => current - 1)}
                  className="rounded-lg border border-outline-variant px-4 py-2.5 text-label-sm font-medium text-primary"
                >
                  {t("onboarding.back")}
                </button>
              ) : null}
              <button
                type="button"
                onClick={next}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-label-sm font-medium text-on-primary"
              >
                {stepIndex >= steps.length - 1 ? t("onboarding.finish") : t("onboarding.continue")}
                <MaterialIcon name="arrow_forward" className="text-[16px]" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientOnboardingPage;
